import type { Item } from '@eshop/types/catalogue.types.ts';
import type { CartItem } from '@eshop/types/cart.types.ts';
import { analytics } from '@tools/analytics.ts';
import { parsePrice } from '@tools/utils/common.tsx';
import type { EventProperties, TrackOptions as AnalyticsTrackOptions } from '@go-analytics/web-sdk';

export type ItemAnalyticsEventName =
  | 'item_viewed'
  | 'item_image_zoomed'
  | 'item_impression'
  | 'cart_item_added'
  | 'checkout_started'
  | 'purchase_completed';

export interface ItemAnalyticsContext {
  surface?: string;
  listInstanceId?: string;
  position?: number;
  source?: string;
}

export interface ItemAnalyticsPayload {
  item_id: string;
  variant_id: string | null;
  item_name: string;
  item_type: string | null;
  item_class_id: string | null;
  category_ids: string[];
  price: number;
  currency: string | null;
  quantity?: number;
  surface?: string;
  list_instance_id?: string;
  position?: number;
  source?: string;
}

export interface ItemImpressionContext extends ItemAnalyticsContext {
  visibleRatio: number;
  visibleTimeMs: number;
  viewportWidth?: number;
  viewportHeight?: number;
  renderedAt?: string;
}

const toStringOrNull = (value: unknown): string | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return String(value);
};

const getParentId = (item: Item): string | null =>
  toStringOrNull(item.parent_id ?? item.parent?.id ?? null);

const getItemClassId = (item: Item): string | null =>
  toStringOrNull(item.item_class_id ?? item.item_class ?? item.parent?.item_class_id ?? item.parent?.item_class);

const getCategoryIds = (item: Item): string[] => {
  const categories = Array.isArray(item.categories) ? item.categories : [];
  return categories
    .map((category) => toStringOrNull(category.id))
    .filter((value): value is string => Boolean(value));
};

export const buildItemAnalyticsPayload = (
  item: Item,
  quantity?: number,
  context: ItemAnalyticsContext = {},
): ItemAnalyticsPayload => {
  const price = parsePrice(item.price);
  return {
    item_id: String(getParentId(item) ?? item.id),
    variant_id: item.is_child || getParentId(item) ? String(item.id) : null,
    item_name: item.fullName ?? item.full_name ?? item.name,
    item_type: item.item_type ?? null,
    item_class_id: getItemClassId(item),
    category_ids: getCategoryIds(item),
    price: price?.value ?? 0,
    currency: price?.currency ?? null,
    ...(typeof quantity === 'number' ? { quantity } : {}),
    ...(context.surface ? { surface: context.surface } : {}),
    ...(context.listInstanceId ? { list_instance_id: context.listInstanceId } : {}),
    ...(typeof context.position === 'number' ? { position: context.position } : {}),
    ...(context.source ? { source: context.source } : {}),
  };
};

export const buildCartItemAnalyticsPayload = (line: CartItem): ItemAnalyticsPayload => {
  if (line.item) {
    return buildItemAnalyticsPayload(line.item, line.quantity);
  }

  const unitPrice = line.quantity > 0 ? Math.round(line.subtotal.total / line.quantity) : line.subtotal.total;
  return {
    item_id: String(line.main_item_id ?? line.item_id),
    variant_id: line.main_item_id && line.main_item_id !== line.item_id ? String(line.item_id) : null,
    item_name: line.name,
    item_type: null,
    item_class_id: null,
    category_ids: [],
    price: unitPrice,
    currency: null,
    quantity: line.quantity,
  };
};

export const buildItemsCollectionPayload = (cartItems: CartItem[]) =>
  cartItems.map(buildCartItemAnalyticsPayload);

const track = (
  eventName: ItemAnalyticsEventName,
  properties: object,
  options?: AnalyticsTrackOptions,
) => {
  analytics?.track(eventName, properties as EventProperties, options);
};

export const trackItemViewed = (item: Item, context: ItemAnalyticsContext = {}) => {
  const payload = buildItemAnalyticsPayload(item, undefined, context);
  track('item_viewed', payload, {
    logicalEventId: `item_viewed:item:${payload.item_id}:variant:${payload.variant_id ?? 'none'}:path:${window.location.pathname}`,
  });
};

export const trackItemImageZoomed = (item: Item, context: ItemAnalyticsContext = {}) => {
  const payload = buildItemAnalyticsPayload(item, undefined, context);
  track('item_image_zoomed', payload, {
    logicalEventId: `item_image_zoomed:item:${payload.item_id}:variant:${payload.variant_id ?? 'none'}:path:${window.location.pathname}`,
  });
};

export const trackCartItemAdded = (
  item: Item,
  quantity: number,
  context: ItemAnalyticsContext = {},
) => {
  const payload = buildItemAnalyticsPayload(item, quantity, context);
  track('cart_item_added', payload, {
    logicalEventId: `cart_item_added:item:${payload.item_id}:variant:${payload.variant_id ?? 'none'}:path:${window.location.pathname}`,
  });
};

export const trackCheckoutStarted = (
  cartId: number | string,
  cartItems: CartItem[],
  value: number,
  currency: string,
) => {
  track('checkout_started', {
    value,
    currency,
    items_count: cartItems.length,
    items: buildItemsCollectionPayload(cartItems),
  }, {
    logicalEventId: `checkout_started:cart:${cartId}`,
  });
};

export const trackPurchaseCompleted = (
  orderId: number | string,
  cartItems: CartItem[],
  value: number,
  currency: string,
) => {
  track('purchase_completed', {
    order_id: String(orderId),
    value,
    currency,
    items_count: cartItems.length,
    items: buildItemsCollectionPayload(cartItems),
  }, {
    logicalEventId: `purchase_completed:order:${orderId}`,
  });
};

export const trackItemImpression = (item: Item, context: ItemImpressionContext) => {
  const payload = buildItemAnalyticsPayload(item, undefined, context);
  track('item_impression', {
    ...payload,
    visible_ratio: context.visibleRatio,
    visible_time_ms: context.visibleTimeMs,
    viewport_width: context.viewportWidth ?? window.innerWidth,
    viewport_height: context.viewportHeight ?? window.innerHeight,
    rendered_at: context.renderedAt ?? new Date().toISOString(),
  }, {
    logicalEventId: `item_impression:${context.listInstanceId ?? 'catalog'}:${payload.item_id}:${payload.variant_id ?? 'none'}:${context.position ?? 'na'}`,
  });
};
